import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback
} from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  InputAdornment,
  Button,
  FormControlLabel,
  Switch
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import { GlobalContext } from '../../utils/ContextProviders/GlobalContextProvider';
import Layout from '../../Component/layout';
import CommunitiesTable, { PAGE_SIZE } from './CommunitiesTable';
import { getCommunities } from '../../Redux/util/Apis/communities';

const Communities = props => {
  const { userData } = useContext(GlobalContext);
  const clients = useMemo(
    () =>
      (userData?.clients || [])
        .slice()
        .sort((a, b) => a.name?.localeCompare(b.name)),
    [userData?.clients]
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [liveOnly, setLiveOnly] = useState(true);
  const [page, setPage] = useState(0);
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  const [rawData, setRawData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  const effectiveClientIds = useMemo(
    () =>
      selectedClientIds.length > 0
        ? selectedClientIds
        : clients.map(c => c._id),
    [selectedClientIds, clients]
  );

  const clientMap = useMemo(() => {
    const map = {};
    clients.forEach(c => {
      map[c._id] = c.name;
    });
    return map;
  }, [clients]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 250);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page on filter change
  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm, selectedClientIds, liveOnly]);

  const fetchCommunities = useCallback(async () => {
    if (effectiveClientIds.length === 0) {
      setRawData([]);
      return;
    }

    setLoading(true);
    try {
      const results = await Promise.all(
        effectiveClientIds.map(clientId =>
          getCommunities(clientId).then(res => ({ clientId, res }))
        )
      );

      const all = [];
      results.forEach(({ clientId, res }) => {
        if (res?.error) return;
        const items = Array.isArray(res) ? res : res?.data || [];
        items.forEach(c => {
          all.push({
            id: c.id,
            name: c.name || '',
            clientId,
            client: clientMap[clientId] || '',
            isLive: c.is_live ?? false,
            isLiveAt: c.is_live_at || null,
            totalUnits: c.total_units ?? null,
            availableUnits: c.available_units ?? null,
            integrations: c.integrations ?? false,
            pmsProvider: c.pms_provider || null,
            crmProvider: c.crm_provider || null,
            communityPhone: c.community_phone || c.phone || '',
            callToTextEnabled: c.call_to_text_enabled ?? false,
            address: c.address?.full_address || '',
            state: c.address?.state_short || '',
            timeZone: c.time_zone || '',
            enableBot: c.enable_bot ?? false,
            isSettingsEnable: c.is_settings_enable ?? false,
            kelseyRestricted: c.kelsey_restricted ?? false
          });
        });
      });

      setRawData(all);
    } catch (err) {
      console.error('Failed to fetch communities:', err);
      setRawData([]);
    } finally {
      setLoading(false);
      setLastRefreshedAt(new Date());
    }
  }, [effectiveClientIds, clientMap]);

  useEffect(() => {
    fetchCommunities();
  }, [fetchCommunities]);

  // Apply client-side filters + sort + paginate
  const filteredAndSorted = useMemo(() => {
    if (!rawData) return [];
    let filtered = rawData;

    if (selectedClientIds.length > 0) {
      filtered = filtered.filter(c => selectedClientIds.includes(c.clientId));
    }
    if (liveOnly) {
      filtered = filtered.filter(c => c.isLive);
    }
    if (debouncedSearchTerm) {
      const q = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter(c =>
        [c.name, c.address, c.communityPhone, c.id, c.client]
          .filter(Boolean)
          .some(v => String(v).toLowerCase().includes(q))
      );
    }

    const sorted = [...filtered].sort((a, b) => {
      const av = a[sortField];
      const bv = b[sortField];
      if (typeof av === 'number' || typeof bv === 'number') {
        return (av ?? 0) - (bv ?? 0);
      }
      if (sortField === 'isLiveAt') {
        const at = av ? new Date(av).getTime() : 0;
        const bt = bv ? new Date(bv).getTime() : 0;
        return at - bt;
      }
      if (sortField === 'isLive') {
        return a.isLive === b.isLive ? 0 : a.isLive ? -1 : 1;
      }
      return String(av || '').localeCompare(String(bv || ''));
    });

    return sortDirection === 'desc' ? sorted.reverse() : sorted;
  }, [
    rawData,
    selectedClientIds,
    liveOnly,
    debouncedSearchTerm,
    sortField,
    sortDirection
  ]);

  const paginatedData = useMemo(
    () => filteredAndSorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [filteredAndSorted, page]
  );

  const handleSortChange = field => {
    if (field === sortField) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setSelectedClientIds([]);
    setLiveOnly(true);
    setPage(0);
  };

  return (
    <Layout history={props.history}>
      <Box
        sx={{
          p: 2,
          pb: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          height: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            my: 1
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            Communities
            {rawData && (
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                ({filteredAndSorted.length}
                {rawData.length !== filteredAndSorted.length
                  ? ` of ${rawData.length}`
                  : ''}
                )
              </Typography>
            )}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'flex-start',
            flexWrap: 'wrap'
          }}
        >
          <TextField
            placeholder="Search by name, address, phone, ID..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              )
            }}
            sx={{
              width: 380,
              minWidth: 380,
              backgroundColor: 'background.paper',
              borderRadius: '4px'
            }}
          />

          <FormControl
            size="small"
            sx={{
              width: 240,
              minWidth: 240,
              backgroundColor: 'background.paper'
            }}
          >
            <InputLabel>Clients</InputLabel>
            <Select
              multiple
              value={selectedClientIds}
              onChange={e => setSelectedClientIds(e.target.value)}
              label="Clients"
              renderValue={selected =>
                selected.length === 0
                  ? ''
                  : selected.map(id => clientMap[id] || id).join(', ')
              }
            >
              {clients.map(c => (
                <MenuItem key={c._id} value={c._id}>
                  <Checkbox checked={selectedClientIds.includes(c._id)} />
                  <ListItemText primary={c.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            onClick={handleClearFilters}
            sx={{
              height: 40,
              whiteSpace: 'nowrap',
              backgroundColor: 'background.paper'
            }}
          >
            Clear Filters
          </Button>
          <Button
            variant="outlined"
            onClick={fetchCommunities}
            sx={{
              height: 40,
              minWidth: 40,
              px: 1,
              backgroundColor: 'background.paper'
            }}
          >
            <RefreshIcon />
          </Button>
          <FormControlLabel
            control={
              <Switch
                checked={liveOnly}
                onChange={e => setLiveOnly(e.target.checked)}
              />
            }
            label="Live only"
          />
        </Box>

        {lastRefreshedAt && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Typography variant="caption" color="text.secondary">
              Refreshed at {lastRefreshedAt.toLocaleTimeString()}
            </Typography>
          </Box>
        )}

        <CommunitiesTable
          data={loading ? null : paginatedData}
          totalCount={filteredAndSorted.length}
          page={page}
          onPageChange={setPage}
          sortField={sortField}
          sortDirection={sortDirection}
          onSortChange={handleSortChange}
          loading={loading && rawData === null}
        />
      </Box>
    </Layout>
  );
};

export default Communities;
